import { AppKeys, getUrlForApp } from '@microsoft/portal-app/lib/environment/getUrlForApp';
import { PortalProvider } from '@microsoft/portal-app/lib/PortalProvider';
import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { Store } from '@microsoft/portal-app/lib/redux/compat';
import { IRootAccountState } from '../../Account/models';
import { msaDiscovery } from '../../msaDiscovery';
import { configureStore, history } from '../../redux/configureStore';
import { ConnectedApp } from './ConnectedApp';
import { getTelemetry } from '../../Telemetry/Telemetry';
import { IGlobalState } from '../../redux/modules/IGlobalState';
import { TwoUITelemetryProvider } from '@amx/ux-twoui-telemetry';
import {
  canShowSystemInitiatedIPF,
  InProductFeedbackDialog,
  getIPFFeatureAreaType,
  getSystemInitiatedIPFSurveyNameBasedOnCurrentPage,
} from '../../InProductFeedback';
import { AppContext, IAppContext } from '../AppProvider';
import { BaseLayout, DialogControlProvider } from '@amx/ux-twoui';
import { Routes } from '../routes/Routes';

const REFERENCE_PARAM = 'ref';

/**
 * The main entry to the application.
 */
export class AccountMainBase extends React.Component<InjectedTranslateProps> {
  static contextType = AppContext;
  constructor(props: InjectedTranslateProps) {
    super(props);
  }
  private store: Store<IGlobalState> = configureStore();
  private initialized?: boolean;

  public componentDidMount(): void {
    msaDiscovery(location.search);
    this.store.subscribe(() => this._init());
    this._init();

    const log = {
      time: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
    };
    const telemetry = getTelemetry();
    telemetry.reportCustomEvent('performance/app-mounted', log);
    this._logReferenceParam();
  }

  public render(): React.ReactNode {
    const appStateKey: keyof IRootAccountState = 'account';
    const AppContextValue: IAppContext = this.context;
    const { IPFState, onHideIPF, onShowIPF } = AppContextValue.InProductFeedbackOption;
    const { isIPFVisible, feedbackItemId } = IPFState;

    return (
      <PortalProvider store={this.store}>
        <ConnectedApp
          appStateKey={appStateKey}
          appBarProps={{
            searchVisible: false,
            waffleVisible: false,
          }}
          suiteHeader={<></>}
          routes={[]}
          history={history}
          getAppTitle={this._getAppTitle}
          accountUrl={getUrlForApp(location, AppKeys.account).toString()}
          onShowInProductFeedback={onShowIPF} // used by left nav in myaccount
        >
          <div style={{ width: '100%', background: '#f2f2f2', overflow: 'auto' }}>
            <TwoUITelemetryProvider twoUITelemetryIds={{ areaBiId: '' }} twoUIFlights={[]}>
              <BaseLayout variant="AMC">
                <DialogControlProvider>
                  <Routes />
                </DialogControlProvider>
              </BaseLayout>
            </TwoUITelemetryProvider>
          </div>
        </ConnectedApp>
        {isIPFVisible && <InProductFeedbackDialog feedbackItemId={feedbackItemId} onDismiss={onHideIPF} />}
      </PortalProvider>
    );
  }

  private readonly _getAppTitle = () => {
    const { t } = this.props;
    return t('My Account');
  };

  private _initSystemInitiatedFeedback(state: IGlobalState) {
    const localStorage = window.localStorage;
    const AppContextvalue = this.context;
    if (state.user.auth.profile && localStorage && AppContextvalue) {
      const surveyName = getSystemInitiatedIPFSurveyNameBasedOnCurrentPage();
      if (
        canShowSystemInitiatedIPF(
          localStorage,
          surveyName,
          state.user.auth.profile.tid,
          state.user.auth.profile.oid,
          getIPFFeatureAreaType(),
        )
      ) {
        AppContextvalue.InProductFeedbackOption.onShowIPF(surveyName);
      }
    }
  }

  private _init(): void {
    const state = this.store!.getState();
    if (!this.initialized && state.user.auth && state.user.auth.isAuthenticated === true) {
      this._initSystemInitiatedFeedback(state);
      this.initialized = true;
    }
  }

  private _logReferenceParam = () => {
    const searchParams = new URLSearchParams(location.search.toLocaleLowerCase());
    const telemetry = getTelemetry();
    if (searchParams.has(REFERENCE_PARAM)) {
      telemetry.reportCustomEvent('overview/reference-param/detected', { source: searchParams.get(REFERENCE_PARAM) });
    }
  };
}

export const AccountMain = translate('Account')(AccountMainBase);
